// V1.5 — Admin Disputas tab.
//
// Lista as disputas abertas (status DISPUTED) cos botóns para resolver
// a favor de comprador (refund + rectificativas) ou vendedor (confirma).
// Ten unha sección secundaria coa historia de disputas resoltas.

import { AlertTriangle, CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import Spinner from '../../components/ui/Spinner';
import { describeAxiosError } from '../../api/errors';
import { useToast } from '../../context/ToastContext';
import { listDisputes, resolveDispute, type AdminDisputeItem } from '../../api/admin-disputes';

function formatEuros(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('gl-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function DisputesTab(): JSX.Element {
  const { addToast } = useToast();
  const [open, setOpen] = useState<AdminDisputeItem[]>([]);
  const [resolved, setResolved] = useState<AdminDisputeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listDisputes();
      setOpen(r.open);
      setResolved(r.resolved);
    } catch (err) {
      addToast(describeAxiosError(err, 'Non se puido cargar disputas'), 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleResolve(
    item: AdminDisputeItem,
    resolution: 'BUYER' | 'SELLER',
  ): Promise<void> {
    const verb = resolution === 'BUYER' ? 'reembolsar ao comprador' : 'confirmar a venda';
    const notes = window.prompt(
      `Notas sobre a resolución (vai para audit log + dispute_resolution).\n` +
        `Resolución: ${verb}.\n\n` +
        `Detalle (mín. 3 caracteres):`,
    );
    if (!notes || notes.trim().length < 3) return;
    setActingId(item.sale_id);
    try {
      const result = await resolveDispute({
        saleId: item.sale_id,
        resolution,
        notes: notes.trim(),
      });
      if (resolution === 'BUYER') {
        addToast(
          `Disputa resolta — refund ${result.refunded ? 'OK' : 'FALLO'}, ${result.rectificatives_emitted} rectificativas emitidas`,
          'success',
        );
      } else {
        addToast('Disputa resolta a favor do vendedor', 'success');
      }
      void refresh();
    } catch (err) {
      addToast(describeAxiosError(err, 'Erro ao resolver'), 'error');
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-dark flex items-center gap-2">
            <AlertTriangle size={20} className="text-amber-600" />
            Disputas marketplace
          </h2>
          <p className="text-sm text-muted mt-1">
            Resolución manual de disputas abertas por compradores nas 72h post-entrega.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refresh()}
          className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-subtle"
        >
          <RefreshCw size={12} className="inline mr-1" /> Refrescar
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted">
          <Spinner size={16} /> Cargando…
        </div>
      ) : (
        <>
          {/* Open disputes */}
          <section>
            <h3 className="text-sm font-semibold text-dark mb-3">Pendentes ({open.length})</h3>
            {open.length === 0 ? (
              <div className="border border-border rounded-2xl p-8 text-center bg-green-50/50">
                <CheckCircle2 size={28} className="mx-auto mb-2 text-green-700" />
                <p className="text-sm font-medium text-dark">Sen disputas abertas</p>
              </div>
            ) : (
              <ul className="space-y-3">
                {open.map((d) => (
                  <li
                    key={d.sale_id}
                    className="border-2 border-amber-300 bg-amber-50/50 rounded-2xl p-4"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-dark">{d.animal_label}</span>
                          <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-amber-200 text-amber-900">
                            DISPUTA
                          </span>
                        </div>
                        <p className="text-xs text-muted mt-1">
                          {d.buyer_name ?? 'Comprador?'} ← {d.seller_name} ·{' '}
                          {formatEuros(d.price_cents)} €
                        </p>
                        <p className="text-xs text-muted">
                          Aberta: {formatDate(d.dispute_opened_at)}
                        </p>
                      </div>
                    </div>
                    {d.dispute_reason && (
                      <div className="bg-white border border-border rounded-xl p-3 mb-3">
                        <p className="text-[10px] uppercase font-medium text-muted mb-1">
                          Motivo do comprador
                        </p>
                        <p className="text-sm text-dark whitespace-pre-wrap">{d.dispute_reason}</p>
                      </div>
                    )}
                    {/* Phase 27 (B-11) — admin precisa acceso aos PDF
                        de cumprimento para reenvialos en requirimentos
                        AEAT/SITRAN. URLs presignadas polo backend. */}
                    {(d.dib_pdf_url || d.sitran_pdf_url) && (
                      <div className="bg-white border border-border rounded-xl p-3 mb-3">
                        <p className="text-[10px] uppercase font-medium text-muted mb-1">
                          Documentación sanitaria
                        </p>
                        <div className="flex flex-wrap gap-3">
                          {d.dib_pdf_url && (
                            <a
                              href={d.dib_pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-rauxa hover:underline"
                            >
                              📄 DIB
                            </a>
                          )}
                          {d.sitran_pdf_url && (
                            <a
                              href={d.sitran_pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-rauxa hover:underline"
                            >
                              📄 Guía SITRAN
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={actingId === d.sale_id}
                        onClick={() => handleResolve(d, 'BUYER')}
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
                        title="Reembolso Stripe + rectificativas automáticas"
                      >
                        {actingId === d.sale_id ? (
                          <Spinner size={14} />
                        ) : (
                          <CheckCircle2 size={16} />
                        )}
                        Reembolsar comprador
                      </button>
                      <button
                        type="button"
                        disabled={actingId === d.sale_id}
                        onClick={() => handleResolve(d, 'SELLER')}
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-rauxa text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-rauxa/90 disabled:opacity-50"
                        title="Confirma venda — pago xa capturado ao vendedor"
                      >
                        {actingId === d.sale_id ? <Spinner size={14} /> : <XCircle size={16} />}
                        Confirmar venda
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Resolved history */}
          <section>
            <h3 className="text-sm font-semibold text-dark mb-3">Historial ({resolved.length})</h3>
            {resolved.length === 0 ? (
              <p className="text-xs text-muted">Aínda non hai disputas resoltas.</p>
            ) : (
              <ul className="border border-border rounded-2xl bg-white divide-y divide-border overflow-hidden">
                {resolved.map((d) => (
                  <li key={d.sale_id} className="p-3 text-sm">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-dark">{d.animal_label}</span>
                        <span className="text-xs text-muted ml-2">
                          {d.buyer_name ?? '—'} ← {d.seller_name}
                        </span>
                      </div>
                      <span className="text-xs text-muted flex-none">
                        {formatDate(d.dispute_resolved_at)}
                      </span>
                    </div>
                    {d.dispute_resolution && (
                      <p className="text-xs text-muted mt-1">{d.dispute_resolution}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
